'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import type { WheelEvent, TouchEvent } from 'react'
import {
  format,
  isAfter,
  isEqual,
  isWithinInterval,
  addMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  isSameDay,
  addDays,
  isWeekend,
} from 'date-fns'
import { formatPriceK } from '~/utils/format'
import { useHotelAvailabilitySearch } from '~/hooks/use-hotel-availability-search'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { isValidDate } from '~/lib/date-utils'
import type { RoomAvailabilitySearch } from '~/types/hotel'

export interface DateRange {
  from?: Date
  to?: Date
}

type DayType = 'HOLIDAY' | 'WEEKEND' | 'NORMAL'

interface ArrivalDepartDateProps {
  hotelId: string
  selectedDates?: DateRange
  onSelect?: (range: DateRange | undefined) => void
  onClose?: () => void
  activeSection?: string
}

export default function ArrivalDeptDate({
  hotelId,
  selectedDates,
  onSelect,
  activeSection,
}: ArrivalDepartDateProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const [currentMonth, setCurrentMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  )
  const [range, setRange] = useState<DateRange>(
    selectedDates || {
      from: today,
      to: tomorrow,
    }
  )
  const calendarRef = useRef<HTMLDivElement>(null)
  const [touchStartY, setTouchStartY] = useState<number | null>(null)
  const [isLongPressing, setIsLongPressing] = useState(false)
  const longPressTimer = useRef<NodeJS.Timeout | null>(null)
  const autoScrollInterval = useRef<NodeJS.Timeout | null>(null)
  const touchStartTime = useRef<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // Get all days shown in calendar including previous and next month days
  const days = useMemo(() => {
    const firstDayOfMonth = startOfMonth(currentMonth)
    const startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 })
    const endDate = addDays(startDate, 41) // 6 weeks - 1

    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      console.error('Invalid date range calculated:', { startDate, endDate })
      return []
    }

    return eachDayOfInterval({ start: startDate, end: endDate })
  }, [currentMonth])

  const { availability, isLoading } = useHotelAvailabilitySearch({
    hotelId,
    fromDate: format(days[0], 'yyyy-MM-dd'),
    toDate: format(days[days.length - 1], 'yyyy-MM-dd'),
    adults: 1,
    children: 0,
  })

  const prices = useMemo(() => {
    if (!availability || !availability?.length) return {}

    const priceMap: Record<string, { price: string; rate: number; type: DayType }> = {}

    // Group prices by date, only consider rooms with availableRooms > 0
    availability.forEach((room: RoomAvailabilitySearch) => {
      if (room.availableRooms <= 0) return
      const dateStr = room.date
      // Skip if rate is null
      if (room.rate === null) return

      if (!priceMap[dateStr] || (priceMap[dateStr] && room.rate < priceMap[dateStr].rate)) {
        priceMap[dateStr] = {
          price: formatPriceK(room.rate, locale),
          rate: room.rate,
          type: (room.dayType as DayType) || 'NORMAL',
        }
      }
    })

    return priceMap
  }, [availability])

  // Xử lý khi chọn một ngày
  const handleSelectDate = (date: Date) => {
    // Kiểm tra tính hợp lệ của ngày
    if (!isValidDate(date)) {
      console.error('Invalid date selected:', date)
      return
    }

    // Không cho chọn ngày trong quá khứ
    if (isPastDate(date)) return

    if (!range.from) {
      // Nếu chưa có ngày bắt đầu, thiết lập ngày đó là from
      const newRange = { from: date }
      setRange(newRange)
      if (onSelect) onSelect(newRange)
    } else if (!range.to && isAfter(date, range.from)) {
      // Nếu đã có from và chọn thêm ngày mới (phải sau from) thì đặt làm to
      const newRange = { from: range.from, to: date }
      setRange(newRange)
      if (onSelect) onSelect(newRange)
    } else {
      // Nếu đã đủ cả from và to hoặc chọn một ngày trước/bằng from, reset lại bắt đầu mới
      const newRange = { from: date }
      setRange(newRange)
      if (onSelect) onSelect(newRange)
    }
  }

  // Cập nhật range khi selected thay đổi từ bên ngoài
  useEffect(() => {
    if (selectedDates) {
      // Validate selectedDates
      const from = selectedDates.from
      const to = selectedDates.to

      if (from && !isValidDate(from)) {
        console.error('Invalid from date in selectedDates:', from)
        return
      }
      if (to && !isValidDate(to)) {
        console.error('Invalid to date in selectedDates:', to)
        return
      }

      const correctedDates = { ...selectedDates }

      // Nếu from là ngày trong quá khứ, sử dụng ngày hiện tại
      if (from && isPastDate(from)) {
        console.warn('Start date is in the past, using today:', from)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        correctedDates.from = today

        // Nếu to cũng tồn tại, đảm bảo nó sau ngày hiện tại
        if (to && to <= today) {
          const tomorrow = new Date(today)
          tomorrow.setDate(today.getDate() + 1)
          correctedDates.to = tomorrow
        }
      }

      // Đảm bảo to luôn sau from
      const correctedFrom = correctedDates.from
      const correctedTo = correctedDates.to
      if (correctedFrom && correctedTo && correctedTo < correctedFrom) {
        console.error('End date is before start date in selectedDates:', correctedDates)
        const newTo = new Date(correctedFrom)
        newTo.setDate(correctedFrom.getDate() + 1)
        correctedDates.to = newTo
      }

      setRange(correctedDates)
    }
  }, [selectedDates])

  // Gọi onSelect với giá trị mặc định khi component mount
  useEffect(() => {
    if (!selectedDates && onSelect && range.from) {
      // Validate default range
      const from = range.from
      const to = range.to

      if (!isValidDate(from)) {
        console.error('Invalid from date in default range:', from)
        return
      }
      if (to && !isValidDate(to)) {
        console.error('Invalid to date in default range:', to)
        return
      }

      const correctedRange = { ...range }

      // Nếu from là ngày trong quá khứ, sử dụng ngày hiện tại
      if (isPastDate(from)) {
        console.warn('Default start date is in the past, using today:', from)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        correctedRange.from = today

        // Nếu to cũng tồn tại, đảm bảo nó sau ngày hiện tại
        if (to && to <= today) {
          const tomorrow = new Date(today)
          tomorrow.setDate(today.getDate() + 1)
          correctedRange.to = tomorrow
        }
      }

      // Đảm bảo to luôn sau from trong giá trị mặc định
      const correctedFrom = correctedRange.from
      const correctedTo = correctedRange.to
      if (correctedFrom && correctedTo && correctedTo < correctedFrom) {
        console.error('End date is before start date in default range:', correctedRange)
        const newTo = new Date(correctedFrom)
        newTo.setDate(correctedFrom.getDate() + 1)
        correctedRange.to = newTo
      }

      setRange(correctedRange)
      onSelect(correctedRange)
    }
  }, [])

  // Cleanup intervals and timers
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current)
      }
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current)
      }
    }
  }, [])

  // Handle touch start with long press detection
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchStartY(e.touches[0].clientY)
    touchStartTime.current = Date.now()

    // Set up long press timer
    longPressTimer.current = setTimeout(() => {
      setIsLongPressing(true)
      // Start auto-scrolling when long press is detected
      autoScrollInterval.current = setInterval(() => {
        if (e.touches[0].clientY > window.innerHeight / 2) {
          goToNextMonth()
        } else {
          goToPreviousMonth()
        }
      }, 500) // Scroll every 500ms while holding
    }, 500) // Detect long press after 500ms
  }

  // Handle touch move
  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!touchStartY) return

    // Clear long press timer if user moves too much
    const moveThreshold = 10
    const touchMoveY = e.touches[0].clientY
    if (Math.abs(touchMoveY - touchStartY) > moveThreshold) {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current)
      }
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current)
        setIsLongPressing(false)
      }
    }
  }

  // Handle touch end
  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (!touchStartY) return

    // Clear all timers
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
    }
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current)
    }

    const touchEndY = e.changedTouches[0].clientY
    const touchDuration = Date.now() - touchStartTime.current
    const diff = touchEndY - touchStartY

    // Only handle quick swipes if not long pressing
    if (!isLongPressing && touchDuration < 300) {
      if (diff < -50) {
        goToNextMonth()
      } else if (diff > 50) {
        goToPreviousMonth()
      }
    }

    setTouchStartY(null)
    setIsLongPressing(false)
  }

  // Ngăn chặn scroll mặc định khi chạm vào calendar
  useEffect(() => {
    const calendar = calendarRef.current
    if (!calendar) return

    const preventScroll = (e: Event) => {
      e.preventDefault()
    }

    calendar.addEventListener('touchmove', preventScroll, { passive: false })

    return () => {
      calendar.removeEventListener('touchmove', preventScroll)
    }
  }, [])

  // Kiểm tra xem ngày có phải là quá khứ không
  const isPastDate = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  // Chuyển sang tháng trước
  const goToPreviousMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = addMonths(prevMonth, -1)

      // Validate new month
      if (!isValidDate(newMonth)) {
        console.error('Invalid previous month calculated:', newMonth)
        return prevMonth
      }

      // Không cho phép chuyển đến tháng trước tháng hiện tại
      if (newMonth < startOfMonth(new Date())) {
        return prevMonth
      }
      return newMonth
    })
  }

  // Kiểm tra xem có thể chuyển đến tháng trước không
  const canGoToPreviousMonth = useMemo(() => {
    const previousMonth = addMonths(currentMonth, -1)

    // Validate previous month
    if (!isValidDate(previousMonth)) {
      console.error('Invalid previous month in check:', previousMonth)
      return false
    }

    return previousMonth >= startOfMonth(new Date())
  }, [currentMonth])

  // Chuyển sang tháng sau
  const goToNextMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = addMonths(prevMonth, 1)

      // Validate new month
      if (!isValidDate(newMonth)) {
        console.error('Invalid next month calculated:', newMonth)
        return prevMonth
      }

      return newMonth
    })
  }

  // Kiểm tra xem ngày có phải là ngày bắt đầu khoảng không
  const isRangeStart = (date: Date) => {
    return range.from && isSameDay(date, range.from)
  }

  // Kiểm tra xem ngày có phải là ngày kết thúc khoảng không
  const isRangeEnd = (date: Date) => {
    return range.to && isSameDay(date, range.to)
  }

  // Kiểm tra xem ngày có thuộc khoảng giữa (không bao gồm ngày đầu và cuối)
  const isInBetweenRange = (date: Date) => {
    if (!range.from || !range.to) return false
    if (isRangeStart(date) || isRangeEnd(date)) return false
    return isWithinInterval(date, { start: range.from, end: range.to })
  }

  // Lấy thông tin giá và loại ngày
  const getDayInfo = (date: Date) => {
    // Validate date before processing
    if (!isValidDate(date)) {
      console.error('Invalid date in getDayInfo:', date)
      return {
        price: '___',
        type: 'NORMAL' as DayType,
      }
    }

    const dateStr = format(date, 'yyyy-MM-dd')
    const dayOfWeek = date.getDay()
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

    if (prices[dateStr]) {
      return prices[dateStr]
    }

    return {
      price: '___',
      type: isWeekend ? 'WEEKEND' : ('NORMAL' as DayType),
    }
  }

  // Lấy class cho ô ngày
  const getDayClass = (date: Date) => {
    // Ngày không thuộc tháng hiện tại - hiển thị mờ
    const isOutsideCurrentMonth = date.getMonth() !== currentMonth.getMonth()
    const isDisabled = isPastDate(date)
    let classes =
      'p-2 cursor-pointer hover:bg-primary-500 hover:text-white hover:rounded-md relative'

    if (isOutsideCurrentMonth) {
      classes += ' opacity-40'
    }

    if (isDisabled) {
      classes = 'p-2 opacity-40 cursor-not-allowed relative'
    }

    // Nếu là ngày bắt đầu hoặc kết thúc của range
    if (!isDisabled && (isRangeStart(date) || isRangeEnd(date))) {
      classes += ' bg-[#855f42] text-white'
    }
    if (!isDisabled && isRangeStart(date)) {
      classes += ' rounded-l-[4px]'
    }
    if (!isDisabled && isRangeEnd(date)) {
      classes += ' rounded-r-[4px]'
    }
    // Nếu là ngày ở giữa range
    else if (!isDisabled && isInBetweenRange(date)) {
      classes += ' bg-[#f9f5f2]'
    }

    return classes
  }

  // Lấy class cho giá
  const getPriceClass = (type: DayType) => {
    switch (type) {
      case 'HOLIDAY':
        return 'text-[#E02424]'
      case 'WEEKEND':
        return 'text-[#047819]'
      case 'NORMAL':
      default:
        return 'text-[#666B74]'
    }
  }

  // Tên các ngày trong tuần
  const weekdays = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN']

  // Auto scroll when calendar becomes active
  useEffect(() => {
    if (activeSection === 'dates') {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [activeSection])

  return (
    <div ref={containerRef} className="w-full p-0">
      {/* Tiêu đề */}
      <h2 className="font-lexend text-base text-secondary-950">{t('booking.checkInOutTitle')}</h2>
      <div className="my-3 h-[1px] w-full bg-[#F3F3F3]"></div>
      {/* Chú thích màu sắc */}
      <div className="mb-2 flex justify-between">
        <div className="flex items-center">
          <div className="mr-2 h-3 w-3 rounded-full bg-red-500"></div>
          <span className="font-lexend text-[10px] font-[350]">{t('calendar.holidayPrice')}</span>
        </div>
        <div className="flex items-center">
          <div className="mr-2 h-3 w-3 rounded-full bg-green-600"></div>
          <span className="font-lexend text-[10px] font-[350]">{t('calendar.weekendPrice')}</span>
        </div>
        <div className="flex items-center">
          <div className="mr-2 h-3 w-3 rounded-full bg-primary-500"></div>
          <span className="font-lexend text-[10px] font-[350]">{t('calendar.weekdayPrice')}</span>
        </div>
      </div>
      <div className="my-3 h-[1px] w-full bg-[#F3F3F3]"></div>
      {/* Header lịch */}
      <div className="mb-1 flex items-center justify-between">
        <button
          onClick={goToPreviousMonth}
          className={`p-1 ${!canGoToPreviousMonth ? 'cursor-not-allowed opacity-40' : ''}`}
          disabled={!canGoToPreviousMonth}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h3 className="font-lexend text-base font-medium">
          {t('calendar.month', {
            month: currentMonth.getMonth() + 1,
            year: currentMonth.getFullYear(),
          })}
        </h3>
        <button onClick={goToNextMonth} className="p-1">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 6L15 12L9 18"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Bọc trong một div có fixed height để tránh scroll trang */}
      <div className="relative isolate overflow-hidden" style={{ touchAction: 'none' }}>
        {/* Calendar grid */}
        <div
          ref={calendarRef}
          className="grid grid-cols-7 gap-1 text-center"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          {/* Tên các ngày trong tuần */}
          {weekdays.map((day, i) => (
            <div key={i} className="py-2 text-[12px] font-medium text-[#666B74]">
              {day}
            </div>
          ))}

          {/* Các ô ngày trong tháng */}
          {days.map((day, i) => {
            const { price, type } = getDayInfo(day)
            const priceClass = getPriceClass(type)
            const isStartOrEndDay = isRangeStart(day) || isRangeEnd(day)

            return (
              <div key={i} className={getDayClass(day)} onClick={() => handleSelectDate(day)}>
                <div className="flex flex-col items-center">
                  <div className="font-lexend text-[12px] font-medium">{format(day, 'dd')}</div>
                  <div className="h-[10px] min-h-[10px]">
                    {isLoading ? (
                      <div className="mx-auto h-[10px] w-9 animate-pulse rounded bg-primary-500" />
                    ) : (
                      <div
                        className={`font-lexend text-[10px] font-light leading-[10px] ${
                          isStartOrEndDay ? 'text-white' : priceClass
                        }`}
                      >
                        {price}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {isLongPressing && (
          <div className="pointer-events-none absolute inset-0 bg-black/5 transition-colors duration-200" />
        )}
      </div>
    </div>
  )
}
